import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import React, { useEffect, useState } from 'react';
import {
    ActivityIndicator,
    Image,
    Modal,
    Platform,
    Pressable,
    ScrollView,
    StyleSheet,
    Text,
    TextInput,
    View,
} from 'react-native';
import { useDropzone } from 'react-dropzone';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';

import { Child } from '../../interface/Child';
import DataOps from '../../data/DataOps';
import { dobToDisplayString } from '../../utils/childUtils';

type Props = {
    child: Child;
    visible?: boolean;
    isNew?: boolean;
    isSaving?: boolean;
    onSave: (child: Child) => Promise<void>;
    onCancel: () => void;
    parentFirstName?: string;
    parentLastName?: string;
    parentPhone?: string;
};

/** Format a Date to YYYY-MM-DD */
const toISODate = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

/** Format YYYY-MM-DD to a readable label */
const formatDOBLabel = (iso: string) => {
    if (!iso) return '';
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

const ChildEditModal: React.FC<Props> = ({
    child,
    visible = true,
    isNew = false,
    isSaving = false,
    onSave,
    onCancel,
    parentFirstName = '',
    parentLastName = '',
    parentPhone = '',
}) => {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [dateOfBirth, setDateOfBirth] = useState('');
    const [showCalendarModal, setShowCalendarModal] = useState(false);
    const [allergies, setAllergies] = useState('');
    const [interests, setInterests] = useState('');
    const [medicalNotes, setMedicalNotes] = useState('');
    const [ecFirstName, setEcFirstName] = useState('');
    const [ecLastName, setEcLastName] = useState('');
    const [ecPhone, setEcPhone] = useState('');
    const [ecRelationship, setEcRelationship] = useState('');
    const [useParentInfo, setUseParentInfo] = useState(false);
    const [showAdditionalInfo, setShowAdditionalInfo] = useState(false);
    const [profileImage, setProfileImage] = useState('');
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [errors, setErrors] = useState<{ dob?: string }>({});

    useEffect(() => {
        setFirstName(child?.firstName || '');
        setLastName(child?.lastName || '');
        setDateOfBirth(dobToDisplayString(child?.dateOfBirth));
        setShowCalendarModal(false);
        setAllergies(Array.isArray(child?.allergies) ? child.allergies.join(', ') : '');
        setInterests(Array.isArray(child?.interests) ? child.interests.join(', ') : '');
        setMedicalNotes(child?.medicalNotes || '');
        setEcFirstName(child?.emergencyContact?.firstName || '');
        setEcLastName(child?.emergencyContact?.lastName || '');
        setEcPhone(child?.emergencyContact?.phone || '');
        setEcRelationship(child?.emergencyContact?.relationship || '');
        setProfileImage(child?.profileImage || '');
        setUseParentInfo(false);
        setErrors({});
    }, [child]);

    const handleUseParentInfo = (checked: boolean) => {
        setUseParentInfo(checked);
        if (checked) {
            setEcFirstName(parentFirstName);
            setEcLastName(parentLastName);
            setEcPhone(parentPhone);
            if (!ecRelationship) setEcRelationship('Parent');
            setErrors(prev => ({ ...prev, ecPhone: undefined }));
        }
    };

    const onDrop = async (acceptedFiles: File[]) => {
        if (!acceptedFiles.length) return;
        setIsUploadingImage(true);
        try {
            const imageId = `${child.userId}_profile_${Date.now()}`;
            const downloadUrl = await DataOps.uploadImageFile(acceptedFiles[0], imageId);
            if (downloadUrl) setProfileImage(downloadUrl);
        } catch (err) {
            console.error('[ChildEditModal] Image upload error:', err);
        } finally {
            setIsUploadingImage(false);
        }
    };

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'] },
        maxFiles: 1,
    });

    const validate = () => {
        const newErrors: typeof errors = {};
        if (!dateOfBirth) newErrors.dob = 'Required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) return;
        const updatedChild: Child = {
            ...child,
            firstName,
            lastName,
            dateOfBirth,
            profileImage: profileImage || child.profileImage || '',
            allergies: allergies.split(',').map(a => a.trim()).filter(Boolean),
            interests: interests.split(',').map(i => i.trim()).filter(Boolean),
            medicalNotes,
            emergencyContact: {
                firstName: ecFirstName,
                lastName: ecLastName,
                phone: ecPhone,
                relationship: ecRelationship,
            },
        };
        await onSave(updatedChild);
    };

    const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || '?';
    const canSave = firstName.trim().length > 0 && lastName.trim().length > 0;
    const hasParentInfo = parentFirstName || parentLastName || parentPhone;
    const calendarValue = dateOfBirth ? new Date(dateOfBirth + 'T00:00:00') : null;

    return (
        <Modal visible={visible} animationType="fade" transparent onRequestClose={onCancel}>
            <View style={styles.modalOverlay}>
                <View style={styles.mainContainer}>
                    <ScrollView
                        style={styles.modalContent}
                        contentContainerStyle={styles.modalScrollContent}
                        keyboardShouldPersistTaps="handled"
                    >
                        <View style={styles.content}>
                            <Text style={styles.sectionTitle}>{isNew ? 'Add Family Member' : 'Edit Member Info'}</Text>

                            {/* Profile Photo */}
                            <View style={styles.photoRow}>
                                <Pressable
                                    onHoverIn={() => setIsHovered(true)}
                                    onHoverOut={() => setIsHovered(false)}
                                    style={styles.avatarPressable}
                                >
                                    <div {...getRootProps()} style={styles.avatarInner as any}>
                                        <input {...getInputProps()} />
                                        {profileImage ? (
                                            <Image source={{ uri: profileImage }} style={styles.avatarImage} />
                                        ) : (
                                            <View style={styles.avatarPlaceholder}>
                                                <Text style={styles.avatarInitials}>{initials}</Text>
                                            </View>
                                        )}
                                        {isUploadingImage && (
                                            <View style={styles.avatarOverlay}>
                                                <ActivityIndicator color="#fff" />
                                            </View>
                                        )}
                                        {!isUploadingImage && isHovered && (
                                            <View style={styles.avatarOverlay}>
                                                <MaterialCommunityIcons name="camera-plus" size={26} color="white" />
                                                <Text style={styles.uploadText}>Upload</Text>
                                            </View>
                                        )}
                                    </div>
                                </Pressable>
                            </View>

                            {/* Name */}
                            <View style={styles.rowContainer}>
                                <View style={[styles.fieldGroup, styles.fieldHalf]}>
                                    <Text style={styles.label}>First Name *</Text>
                                    <TextInput
                                        style={styles.input}
                                        value={firstName}
                                        onChangeText={setFirstName}
                                        placeholder="First name"
                                        placeholderTextColor="#C4C9D4"
                                        editable={!isSaving}
                                    />
                                </View>
                                <View style={[styles.fieldGroup, styles.fieldHalf]}>
                                    <Text style={styles.label}>Last Name *</Text>
                                    <TextInput
                                        style={styles.input}
                                        value={lastName}
                                        onChangeText={setLastName}
                                        placeholder="Last name"
                                        placeholderTextColor="#C4C9D4"
                                        editable={!isSaving}
                                    />
                                </View>
                            </View>

                            {/* Date of birth */}
                            <View style={styles.fieldGroup}>
                                <View style={styles.labelRow}>
                                    <Text style={styles.label}>Date of Birth *</Text>
                                    {errors.dob && <Text style={styles.errorTag}>{errors.dob}</Text>}
                                </View>
                                <Pressable
                                    style={[styles.dateButton, errors.dob && styles.inputError]}
                                    onPress={() => setShowCalendarModal(true)}
                                    disabled={isSaving}
                                >
                                    <MaterialCommunityIcons
                                        name="calendar-month-outline"
                                        size={17}
                                        color={dateOfBirth ? '#3C8248' : '#9CA3AF'}
                                        style={{ marginRight: 8 }}
                                    />
                                    <Text style={[styles.dateButtonText, !dateOfBirth && styles.dateButtonPlaceholder]}>
                                        {dateOfBirth ? formatDOBLabel(dateOfBirth) : 'Select date of birth'}
                                    </Text>
                                    <MaterialCommunityIcons name="chevron-down" size={16} color="#9CA3AF" />
                                </Pressable>
                            </View>

                            {/* Emergency Contact */}
                            <View style={styles.sectionDivider} />
                            <View style={styles.ecHeader}>
                                <View>
                                    <Text style={styles.sectionSubtitle}>Emergency Contact *</Text>
                                    <Text style={styles.collapsedSubtext}>Contacted in case of emergency</Text>
                                </View>
                                {hasParentInfo && (
                                    <Pressable
                                        style={styles.checkboxRow}
                                        onPress={() => handleUseParentInfo(!useParentInfo)}
                                    >
                                        <View style={[styles.checkbox, useParentInfo && styles.checkboxChecked]}>
                                            {useParentInfo && (
                                                <MaterialCommunityIcons name="check" size={11} color="#fff" />
                                            )}
                                        </View>
                                        <Text style={styles.checkboxLabel}>same as registering parent</Text>
                                    </Pressable>
                                )}
                            </View>

                            <View style={styles.rowContainer}>
                                <View style={[styles.fieldGroup, styles.fieldHalf]}>
                                    <Text style={styles.label}>First Name</Text>
                                    <TextInput
                                        style={[styles.input, useParentInfo && styles.inputReadOnly]}
                                        value={ecFirstName}
                                        onChangeText={v => { setEcFirstName(v); setUseParentInfo(false); }}
                                        placeholder="First name"
                                        placeholderTextColor="#C4C9D4"
                                        editable={!isSaving && !useParentInfo}
                                    />
                                </View>
                                <View style={[styles.fieldGroup, styles.fieldHalf]}>
                                    <Text style={styles.label}>Last Name</Text>
                                    <TextInput
                                        style={[styles.input, useParentInfo && styles.inputReadOnly]}
                                        value={ecLastName}
                                        onChangeText={v => { setEcLastName(v); setUseParentInfo(false); }}
                                        placeholder="Last name"
                                        placeholderTextColor="#C4C9D4"
                                        editable={!isSaving && !useParentInfo}
                                    />
                                </View>
                            </View>
                            <View style={styles.rowContainer}>
                                <View style={[styles.fieldGroup, styles.fieldHalf]}>
                                    <Text style={styles.label}>Phone</Text>
                                    <TextInput
                                        style={[styles.input, useParentInfo && styles.inputReadOnly]}
                                        value={ecPhone}
                                        onChangeText={v => { setEcPhone(v); setUseParentInfo(false); }}
                                        placeholder="Phone number"
                                        placeholderTextColor="#C4C9D4"
                                        editable={!isSaving && !useParentInfo}
                                    />
                                </View>
                                <View style={[styles.fieldGroup, styles.fieldHalf]}>
                                    <Text style={styles.label}>Relationship</Text>
                                    <TextInput
                                        style={styles.input}
                                        value={ecRelationship}
                                        onChangeText={setEcRelationship}
                                        placeholder="e.g. Parent"
                                        placeholderTextColor="#C4C9D4"
                                        editable={!isSaving}
                                    />
                                </View>
                            </View>

                            {/* Additional Info — collapsible, optional */}
                            <View style={styles.sectionDivider} />
                            <Pressable style={styles.collapsibleHeader} onPress={() => setShowAdditionalInfo(v => !v)}>
                                <View>
                                    <Text style={styles.sectionSubtitle}>Additional Info</Text>
                                    {!showAdditionalInfo && (
                                        <Text style={styles.collapsedSubtext}>Allergies, interests, medical notes</Text>
                                    )}
                                </View>
                                <MaterialCommunityIcons
                                    name={showAdditionalInfo ? 'chevron-up' : 'chevron-down'}
                                    size={20}
                                    color="#9CA3AF"
                                />
                            </Pressable>

                            {showAdditionalInfo && (
                                <>
                                    <View style={styles.fieldGroup}>
                                        <Text style={styles.label}>Allergies</Text>
                                        <TextInput
                                            style={[styles.input, styles.multilineInput]}
                                            value={allergies}
                                            onChangeText={setAllergies}
                                            placeholder="e.g. peanuts, dairy, gluten"
                                            placeholderTextColor="#C4C9D4"
                                            multiline
                                            editable={!isSaving}
                                        />
                                    </View>
                                    <View style={styles.fieldGroup}>
                                        <Text style={styles.label}>Interests</Text>
                                        <TextInput
                                            style={[styles.input, styles.multilineInput]}
                                            value={interests}
                                            onChangeText={setInterests}
                                            placeholder="e.g. soccer, art, swimming"
                                            placeholderTextColor="#C4C9D4"
                                            multiline
                                            editable={!isSaving}
                                        />
                                    </View>
                                    <View style={styles.fieldGroup}>
                                        <Text style={styles.label}>Medical Notes</Text>
                                        <TextInput
                                            style={[styles.input, styles.multilineInput]}
                                            value={medicalNotes}
                                            onChangeText={setMedicalNotes}
                                            placeholder="Any conditions, medications or special instructions"
                                            placeholderTextColor="#C4C9D4"
                                            multiline
                                            editable={!isSaving}
                                        />
                                    </View>
                                </>
                            )}
                        </View>
                    </ScrollView>

                    <View style={styles.actions}>
                        <Pressable
                            style={[styles.button, styles.saveBtn, (!canSave || isSaving) && styles.saveBtnDisabled]}
                            onPress={handleSave}
                            disabled={!canSave || isSaving}
                        >
                            {isSaving ? (
                                <ActivityIndicator size="small" color="#FFFFFF" />
                            ) : (
                                <Text style={styles.saveBtnText}>{isNew ? 'Add Member' : 'Save Changes'}</Text>
                            )}
                        </Pressable>
                        <Pressable
                            style={[styles.button, styles.cancelBtn]}
                            onPress={onCancel}
                            disabled={isSaving}
                        >
                            <Text style={styles.cancelBtnText}>Cancel</Text>
                        </Pressable>
                    </View>
                </View>
            </View>

            {/* Calendar picker — separate modal so it's never clipped by ScrollView */}
            <Modal
                visible={showCalendarModal}
                transparent
                animationType="fade"
                onRequestClose={() => setShowCalendarModal(false)}
            >
                {Platform.OS === 'web' && (
                    <style>{`
                        .child-dob-calendar {
                            border: none !important;
                            border-radius: 0 !important;
                            font-family: inherit !important;
                            padding: 12px 16px 20px !important;
                            background: #fff !important;
                            width: 380px !important;
                        }
                        .child-dob-calendar .react-calendar__navigation {
                            margin-bottom: 12px !important;
                            height: auto !important;
                        }
                        .child-dob-calendar .react-calendar__navigation button {
                            font-size: 15px !important;
                            font-weight: 700 !important;
                            color: #0F172A !important;
                            border-radius: 8px !important;
                            min-width: 40px !important;
                            padding: 8px !important;
                        }
                        .child-dob-calendar .react-calendar__navigation button:hover {
                            background: #F0FDF4 !important;
                            color: #166534 !important;
                        }
                        .child-dob-calendar .react-calendar__month-view__weekdays {
                            margin-bottom: 4px !important;
                        }
                        .child-dob-calendar .react-calendar__month-view__weekdays__weekday abbr {
                            text-decoration: none !important;
                            font-size: 12px !important;
                            font-weight: 600 !important;
                            color: #94A3B8 !important;
                        }
                        .child-dob-calendar .react-calendar__tile {
                            border-radius: 10px !important;
                            font-size: 14px !important;
                            color: #0F172A !important;
                            padding: 11px 6px !important;
                            line-height: 1.2 !important;
                        }
                        .child-dob-calendar .react-calendar__tile:hover {
                            background: #F0FDF4 !important;
                            color: #166534 !important;
                        }
                        .child-dob-calendar .react-calendar__tile--active,
                        .child-dob-calendar .react-calendar__tile--active:hover {
                            background: #3C8248 !important;
                            color: #fff !important;
                            font-weight: 700 !important;
                        }
                        .child-dob-calendar .react-calendar__tile--now {
                            background: #ECFDF5 !important;
                            color: #166534 !important;
                            font-weight: 700 !important;
                        }
                        .child-dob-calendar .react-calendar__tile--now.react-calendar__tile--active {
                            background: #3C8248 !important;
                            color: #fff !important;
                        }
                        .child-dob-calendar .react-calendar__tile:disabled {
                            color: #D1D5DB !important;
                            background: transparent !important;
                        }
                        .child-dob-calendar .react-calendar__year-view .react-calendar__tile,
                        .child-dob-calendar .react-calendar__decade-view .react-calendar__tile,
                        .child-dob-calendar .react-calendar__century-view .react-calendar__tile {
                            padding: 18px 8px !important;
                            font-size: 14px !important;
                        }
                    `}</style>
                )}
                <Pressable style={styles.calendarOverlay} onPress={() => setShowCalendarModal(false)}>
                    <Pressable style={styles.calendarCard} onPress={e => e.stopPropagation()}>
                        <View style={styles.calendarHeader}>
                            <Text style={styles.calendarTitle}>Date of Birth</Text>
                            <Pressable onPress={() => setShowCalendarModal(false)} style={styles.calendarClose}>
                                <MaterialCommunityIcons name="close" size={20} color="#64748B" />
                            </Pressable>
                        </View>
                        <Calendar
                            className="child-dob-calendar"
                            value={calendarValue}
                            maxDate={new Date()}
                            defaultActiveStartDate={
                                calendarValue || new Date(new Date().getFullYear() - 8, 0, 1)
                            }
                            onChange={(val: any) => {
                                if (val instanceof Date) {
                                    setDateOfBirth(toISODate(val));
                                    setErrors(prev => ({ ...prev, dob: undefined }));
                                    setShowCalendarModal(false);
                                }
                            }}
                        />
                    </Pressable>
                </Pressable>
            </Modal>
        </Modal>
    );
};

const styles = StyleSheet.create({
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.45)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
    },
    mainContainer: {
        width: '100%',
        maxWidth: 560,
        maxHeight: '90%',
        backgroundColor: '#FFFFFF',
        borderRadius: 20,
        overflow: 'hidden',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 16 },
        shadowOpacity: 0.18,
        shadowRadius: 32,
        elevation: 20,
    },
    modalContent: {
        flexGrow: 0,
        flexShrink: 1,
    },
    modalScrollContent: {},
    content: {
        padding: 24,
    },
    sectionTitle: {
        fontSize: 18,
        fontWeight: '700',
        color: '#0F172A',
        marginBottom: 20,
        letterSpacing: -0.3,
    },

    // Photo
    photoRow: {
        alignItems: 'center',
        marginBottom: 24,
    },
    avatarPressable: {
        borderRadius: 50,
    },
    avatarInner: {
        width: 96,
        height: 96,
        borderRadius: 48,
        backgroundColor: '#F1F5F9',
        overflow: 'hidden',
        position: 'relative',
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        cursor: 'pointer',
    } as any,
    avatarImage: {
        width: '100%',
        height: '100%',
    },
    avatarPlaceholder: {
        width: '100%',
        height: '100%',
        backgroundColor: '#3C8248',
        alignItems: 'center',
        justifyContent: 'center',
    },
    avatarInitials: {
        color: '#FFF',
        fontWeight: '700',
        fontSize: 30,
    },
    avatarOverlay: {
        position: 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.42)',
        alignItems: 'center',
        justifyContent: 'center',
    },
    uploadText: {
        color: 'white',
        fontSize: 10,
        fontWeight: '700',
        marginTop: 2,
        letterSpacing: 0.5,
    },

    // Fields
    labelRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        marginBottom: 5,
    },
    rowContainer: {
        flexDirection: 'row',
        gap: 12,
        marginBottom: 16,
    },
    fieldGroup: {
        marginBottom: 16,
    },
    fieldHalf: {
        flex: 1,
    },
    label: {
        fontSize: 11,
        fontWeight: '600',
        color: '#94A3B8',
        marginBottom: 5,
        letterSpacing: 0.5,
        textTransform: 'uppercase',
    },
    input: {
        borderWidth: 1.5,
        borderColor: '#E2E8F0',
        borderRadius: 10,
        paddingHorizontal: 13,
        paddingVertical: 10,
        fontSize: 14,
        backgroundColor: '#FAFBFC',
        color: '#0F172A',
    },
    inputReadOnly: {
        backgroundColor: '#F8FAFC',
        color: '#94A3B8',
        borderColor: '#EEF2F7',
    },
    inputError: {
        borderColor: '#F87171',
        backgroundColor: '#FFF5F5',
    },
    multilineInput: {
        minHeight: 72,
        textAlignVertical: 'top',
    },
    errorTag: {
        fontSize: 11,
        fontWeight: '600',
        color: '#EF4444',
        backgroundColor: '#FEF2F2',
        paddingHorizontal: 6,
        paddingVertical: 2,
        borderRadius: 4,
    },

    // Date button
    dateButton: {
        flexDirection: 'row',
        alignItems: 'center',
        borderWidth: 1.5,
        borderColor: '#E2E8F0',
        borderRadius: 10,
        paddingHorizontal: 13,
        paddingVertical: 10,
        backgroundColor: '#FAFBFC',
    },
    dateButtonText: {
        flex: 1,
        fontSize: 14,
        color: '#0F172A',
    },
    dateButtonPlaceholder: {
        color: '#C4C9D4',
    },

    // EC section
    sectionDivider: {
        height: 1,
        backgroundColor: '#F1F5F9',
        marginTop: 4,
        marginBottom: 4,
    },
    ecHeader: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        paddingTop: 10,
        paddingBottom: 12,
        gap: 8,
    },
    checkboxRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5,
        paddingTop: 2,
    },
    checkbox: {
        width: 14,
        height: 14,
        borderRadius: 3,
        borderWidth: 1.5,
        borderColor: '#CBD5E1',
        backgroundColor: '#fff',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    checkboxChecked: {
        backgroundColor: '#3C8248',
        borderColor: '#3C8248',
    },
    checkboxLabel: {
        fontSize: 11,
        fontWeight: '500',
        color: '#94A3B8',
    },

    // Collapsible
    collapsibleHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingTop: 10,
        paddingBottom: 12,
        marginBottom: 0,
    },
    sectionSubtitle: {
        fontSize: 13,
        fontWeight: '600',
        color: '#374151',
        letterSpacing: 0.1,
    },
    collapsedSubtext: {
        fontSize: 12,
        color: '#9CA3AF',
        marginTop: 2,
    },

    // Calendar modal
    calendarOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.5)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 24,
    },
    calendarCard: {
        backgroundColor: '#fff',
        borderRadius: 20,
        overflow: 'hidden',
        width: 412,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 12 },
        shadowOpacity: 0.2,
        shadowRadius: 24,
        elevation: 16,
    },
    calendarHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 20,
        paddingTop: 18,
        paddingBottom: 4,
    },
    calendarTitle: {
        fontSize: 15,
        fontWeight: '700',
        color: '#0F172A',
    },
    calendarClose: {
        width: 32,
        height: 32,
        borderRadius: 16,
        backgroundColor: '#F1F5F9',
        alignItems: 'center',
        justifyContent: 'center',
    },

    // Actions
    actions: {
        flexDirection: 'row',
        gap: 10,
        paddingHorizontal: 24,
        paddingVertical: 16,
        backgroundColor: '#FAFBFC',
        borderTopWidth: 1,
        borderColor: '#F1F5F9',
    },
    button: {
        flex: 1,
        paddingVertical: 12,
        borderRadius: 10,
        alignItems: 'center',
        justifyContent: 'center',
    },
    cancelBtn: {
        backgroundColor: '#fff',
        borderWidth: 1.5,
        borderColor: '#E2E8F0',
    },
    cancelBtnText: {
        fontSize: 14,
        fontWeight: '600',
        color: '#64748B',
    },
    saveBtn: {
        backgroundColor: '#3C8248',
    },
    saveBtnDisabled: {
        backgroundColor: '#CBD5E1',
        opacity: 0.7,
    },
    saveBtnText: {
        fontSize: 14,
        fontWeight: '700',
        color: '#FFFFFF',
        letterSpacing: 0.2,
    },
});

export default ChildEditModal;
